import { DateRange } from '../compiled-protos/common';
import { type TimesheetDepartment } from '../TimesheetDepartment';

export function makeDateRange(field: string, start: string, end?: string) {
  const res = DateRange.create();
  const reg = new RegExp(/>= |> |< |<= |like |= /g);
  const dateReg = new RegExp(/\d{4}-\d{2}-\d{2}/g);
  if (reg.test(start) && dateReg.test(start)) {
    res.start = start;
  } else {
    console.warn(
      'Invalid date string detected; date strings must contain a comparison operator following a date in the format YYYY-MM-DD'
    );
  }
  res.field = field;
  res.start = start;
  if (end) {
    res.end = end;
  }
  return res;
}

/**
 * Resolves the device's current position, or rejects if it cannot be obtained.
 *
 * `getCurrentPosition` reports every failure — permission denied, position
 * unavailable, timeout — to its *error callback*, never by throwing. Omitting
 * that callback does not surface an error; it leaves the promise permanently
 * unsettled, so `await getPosition()` hangs forever and no `try/catch` around
 * it can help. The browser's default `timeout` is also `Infinity`, which can
 * hang the same way with no denial involved.
 *
 * Rejects with a `GeolocationPositionError`, whose `code` separates
 * `PERMISSION_DENIED` (1) from `POSITION_UNAVAILABLE` (2) and `TIMEOUT` (3).
 * Callers that need different copy per cause should branch on it — a denial in
 * particular cannot be re-prompted programmatically on iOS Safari, so the user
 * has to be told to change it in Settings.
 *
 * @param options merged over the defaults, so a caller can trade accuracy for
 * speed (`enableHighAccuracy: false`) or accept a cached fix (`maximumAge`).
 */
export function getPosition(options?: PositionOptions) {
  return new Promise<GeolocationPosition>((resolve, reject) => {
    const geo =
      typeof navigator !== 'undefined' ? navigator.geolocation : undefined;
    if (!geo) {
      reject(new Error('Geolocation is unavailable in this environment'));
      return;
    }
    geo.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0,
      ...options,
    });
  });
}

/**
 *
 * @param dateOnly if true, returns only the date portion YYYY-MM-DD
 * @returns a timestamp in the format YYYY-MM-DD HH:MM:SS
 */
export function timestamp(dateOnly = false, date?: Date) {
  const dateObj = date || new Date();
  let month = `${dateObj.getMonth() + 1}`;
  if (month.length === 1) {
    month = `0${month}`;
  }
  let day = `${dateObj.getDate()}`;
  if (day.length === 1) {
    day = `0${day}`;
  }
  let hour = `${dateObj.getHours()}`;
  if (hour.length === 1) {
    hour = `0${hour}`;
  }
  let minute = `${dateObj.getMinutes()}`;
  if (minute.length === 1) {
    minute = `0${minute}`;
  }
  let second = `${dateObj.getSeconds()}`;
  if (second.length === 1) {
    second = `0${second}`;
  }

  if (dateOnly) {
    return `${dateObj.getFullYear()}-${month}-${day}`;
  }
  return `${dateObj.getFullYear()}-${month}-${day} ${hour}:${minute}:${second}`;
}

export function unique<T>(original: T[]) {
  const container: T[] = [];
  for (const el of original) {
    if (!container.includes(el)) {
      container.push(el);
    }
  }
  return container;
}

export const getDepartmentName = (d?: TimesheetDepartment): string =>
  d ? `${d.value} - ${d.description}` : '';

export function getMimeType(fileName: string) {
  const arr = fileName.split('.');
  const ext = arr[arr.length - 1];
  if (ext === 'pdf') {
    return 'application/pdf';
  } else if (ext === 'doc' || ext === 'docx') {
    return 'application/msword';
  } else if (ext === 'png') {
    return 'image/png';
  } else if (ext === 'jpg' || ext === 'jpeg') {
    return 'image/jpeg';
  }
  return undefined
}

export function b64toBlob(b64Data: string, fileName: string) {
  const sliceSize = 512;
  const byteCharacters = atob(b64Data);
  const byteArrays: Uint8Array[] = [];
  const contentType = getMimeType(fileName);

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
}

export type OrderDir = 'ASC' | 'DESC';

export { DateRange } from '../compiled-protos/common';
export default {
  makeDateRange,
  getPosition,
  timestamp,
  unique,
  getDepartmentName,
  getMimeType,
  b64toBlob,
};
